// Waypoint mutations — add / insert / move / delete / sharp toggle.
// Each operation that commits a structural change pushes a history
// snapshot via the supplied `notify` callback.
//
// `moveWaypoint` is the exception: drags push a single snapshot on
// mouseup (handled by the caller), so per-tick mutations during a
// drag don't bloat the undo stack.

import { activeWaypoints, setSelection, clearSelection } from './state.js';
import { pushHistory } from './history.js';

export function addWaypointAtEnd(state, worldPos, notify) {
  const wps = activeWaypoints(state);
  wps.push({ x: worldPos.x, y: worldPos.y });
  setSelection(state, [wps.length - 1]);
  pushHistory(state, notify);
}

export function insertWaypointAt(state, segIdx, worldPos, notify) {
  const wps = activeWaypoints(state);
  wps.splice(segIdx + 1, 0, { x: worldPos.x, y: worldPos.y });
  setSelection(state, [segIdx + 1]);
  pushHistory(state, notify);
}

export function deleteWaypoint(state, idx, notify) {
  activeWaypoints(state).splice(idx, 1);
  clearSelection(state);
  state.drag = null;
  pushHistory(state, notify);
}

export function deleteSelected(state, notify) {
  const indices = [...state.selectedIndices].sort((a, b) => b - a);
  if (indices.length === 0) return;
  const wps = activeWaypoints(state);
  for (const i of indices) wps.splice(i, 1);
  clearSelection(state);
  state.drag = null;
  pushHistory(state, notify);
}

// Toggle: if all selected are sharp, clear all; otherwise mark all as
// sharp. This matches the standalone editor's behavior so a mixed
// selection becomes uniformly sharp on first press, then uniformly
// smooth on second press.
export function toggleSharpOnSelected(state, notify) {
  if (state.selectedIndices.length === 0) return;
  const wps = activeWaypoints(state);
  const allSharp = state.selectedIndices.every(i => !!wps[i]?.sharp);
  for (const i of state.selectedIndices) {
    const wp = wps[i];
    if (!wp) continue;
    if (allSharp) delete wp.sharp;
    else wp.sharp = true;
  }
  pushHistory(state, notify);
}

// Per-tick during a drag — does not push history. Caller commits one
// snapshot on mouseup.
export function moveWaypoint(state, idx, worldPos) {
  const wp = activeWaypoints(state)[idx];
  if (!wp) return;
  wp.x = worldPos.x;
  wp.y = worldPos.y;
}

// Per-waypoint width override. Pass null/undefined to clear the
// override (the waypoint falls back to the trackline default).
// Applies to every selected waypoint in the active entity. Skips the
// pushHistory in the Live variant so a slider drag is one undo step.
export function setSelectedWidthLive(state, width) {
  const wps = activeWaypoints(state);
  for (const i of state.selectedIndices) {
    const wp = wps[i];
    if (!wp) continue;
    if (width == null) delete wp.width;
    else wp.width = width;
  }
}

export function setSelectedWidth(state, width, notify) {
  setSelectedWidthLive(state, width);
  pushHistory(state, notify);
}
